import { FC } from 'react';

import {
  Heading,
  Icon,
  IconName,
  Stack,
  Text,
  cn,
  isValidIconName,
} from '@eluve/components';
import { type MessageKey, useTranslation } from '@eluve/i18n';

const variantStyles = {
  error: {
    iconBg: 'bg-bg-negative-subtle',
    iconText: 'text-text-negative-subtle',
    border: 'border-border-negative-subtle',
    bg: 'bg-bg-negative-subtle',
  },
  warning: {
    iconBg: 'bg-bg-warning-subtle',
    iconText: 'text-text-warning-subtle',
    border: 'border-border-warning-subtle',
    bg: 'bg-bg-warning-subtle',
  },
  info: {
    iconBg: 'bg-bg-quaternary',
    iconText: 'text-text-tertiary',
    border: 'border-border-primary',
    bg: 'bg-bg-secondary',
  },
} as const;

export interface AlertRendererProps {
  variant: 'error' | 'warning' | 'info';
  icon?: string;
  title?: string;
  message?: string;
  /**
   * `@eluve/i18n` message key. When set, the renderer resolves it via
   * `useTranslation` and renders the translated string in place of `title`.
   * Recipe builders that need localized output should set this instead of
   * `title`; see `libs/shared/sdui-types/src/lib/recipes/pvi-states.ts`.
   */
  titleKey?: string;
  /** See {@link AlertRendererProps.titleKey}; same contract for `message`. */
  messageKey?: string;
  filled?: boolean;
}

export const AlertRenderer: FC<AlertRendererProps> = ({
  variant,
  icon,
  title,
  message,
  titleKey,
  messageKey,
  filled,
}) => {
  const { t } = useTranslation();
  const styles = variantStyles[variant];
  const validIcon = icon && isValidIconName(icon);

  // Spec carries `titleKey`/`messageKey` as plain strings; cast through
  // `MessageKey` at the renderer boundary. The bundle's missing-key fallback
  // (returns the key itself) keeps unknown keys visually loud.
  const resolvedTitle = titleKey ? t(titleKey as MessageKey) : (title ?? '');
  const resolvedMessage = messageKey
    ? t(messageKey as MessageKey)
    : (message ?? '');

  const content = (
    <Stack align="center" gap={4}>
      {validIcon && (
        <div
          className={cn(
            'flex size-14 items-center justify-center rounded-xl',
            styles.iconBg,
          )}
        >
          <Icon
            name={icon as IconName}
            size="2xl"
            className={styles.iconText}
          />
        </div>
      )}
      <Heading size="lg">{resolvedTitle}</Heading>
      <Text size="xs" color="tertiary" className="text-center">
        {resolvedMessage}
      </Text>
    </Stack>
  );

  if (filled) {
    return (
      <div
        className={cn(
          'flex h-full min-h-0 flex-1 flex-col items-center justify-center rounded-lg border p-6',
          styles.border,
          styles.bg,
        )}
      >
        {content}
      </div>
    );
  }

  return (
    <Stack
      className="min-h-0 flex-1 px-6"
      fullWidth={false}
      fullHeight
      align="center"
      justify="center"
    >
      {content}
    </Stack>
  );
};
